/**
 * Module-contributed global-search sections (需求 FR-14 §5.3.2 注册制展示形态).
 *
 * A **first-class contribution point**, not one of the generic Layer-0 points
 * (见 17): the Omnibar is platform-defined and platform-rendered, so search
 * belongs with `commands` / `views` / `activityBar` rather than with `ai.tool`,
 * where the owning *module* both defines and consumes the point.
 *
 * The price of being first-class is that every field below is kernel semantics,
 * so the field set stays business-agnostic: nothing here knows what "Messages"
 * or "Files" mean. The platform renders sections it cannot interpret, and
 * {@link SearchContribution.title} is a `%key%` it resolves without
 * understanding.
 *
 * Two phases, exactly as commands work (架构方案 §5.9): the manifest declaration
 * is discoverable **without activating the module** — which is what lets the
 * app tab strip be computed on the first frame — while the behaviour binds at
 * activation, as a registered command. Invocation therefore rides the one
 * existing renderer→ext-host route, `executeCommand`, and lazy activation comes
 * from its `onCommand:<id>` event: IM answers a search without ever having been
 * opened.
 */
import type { IconSpec, ModuleRuntime } from './index';

/**
 * Which tab a section answers under.
 *
 * `app` (the default) is the original behaviour: the section lives inside the
 * declaring module's own tab, and the All tab stays free of cross-process calls
 * entirely. `all` puts it on the All tab instead, for content a user expects to
 * find without first knowing which app owns it — people and groups, as opposed
 * to "IM's messages".
 *
 * A section on the All tab **is only asked once the query is non-empty**
 * (`providerAnswers`), which is what preserves 需求 §4.3's "opening the box is
 * instant": the first frame still issues nothing.
 */
export type SearchScope = 'app' | 'all' | 'both';

/** Whether `scope` (defaulting to `app`) covers the given tab. */
export function scopeCovers(
  scope: SearchScope | undefined,
  tab: 'app' | 'all',
): boolean {
  const declared = scope ?? 'app';
  return declared === 'both' || declared === tab;
}

/**
 * Per-tab display order. Either side may be omitted; the missing side is 0,
 * same as a section that never declared {@link SearchContribution.order}.
 */
export interface SearchOrderByScope {
  app?: number;
  all?: number;
}

/**
 * A section's display order: one number shared by every tab it answers on, or
 * `{ app, all }` when the two tabs want different seats (the reason
 * `scope: "both"` exists without forcing the same rank in both places).
 */
export type SearchOrder = number | SearchOrderByScope;

/**
 * The number used to sort a section on `tab`.
 *
 * A bare number applies to both tabs. An object uses the matching side; an
 * omitted or non-finite side is 0 — the same default a missing `order` already
 * had, not a guess at the other side.
 */
export function resolveSearchOrder(
  order: SearchOrder | undefined,
  tab: 'app' | 'all',
): number {
  if (typeof order === 'number' && Number.isFinite(order)) {
    return order;
  }
  if (order && typeof order === 'object') {
    const scoped = tab === 'all' ? order.all : order.app;
    if (typeof scoped === 'number' && Number.isFinite(scoped)) {
      return scoped;
    }
  }
  return 0;
}

/**
 * One result section a module answers queries for. A module declares one entry
 * per section (IM: Messages and Files), because a section is the unit the
 * platform renders, orders and reports failures against — one flaky engine call
 * then costs its own section rather than the module's whole tab.
 */
export interface SearchContribution {
  /** Section id, unique within the declaring module. */
  id: string;
  /** Section heading, `%key%` placeholder form (架构方案 §5.3). */
  title: string;
  /**
   * Command answering a query for this section. Receives one
   * {@link ModuleSearchRequest} argument and must resolve to
   * {@link ModuleSearchHit}[].
   *
   * Must be declared in the same manifest's `contributes.commands` —
   * `validateManifest` enforces it, because an undeclared command has no
   * `onCommand:<id>` event, so a module that is not already active could never
   * be pulled up and the section would simply stay empty forever. Leave it
   * default-hidden (or mark `internal: true`) so it stays out of the command
   * list; only `internal: false` is listed.
   */
  command: string;
  /**
   * Ascending display order. A number is shared by every tab this section
   * answers on; `{ app, all }` sets each tab separately. Ties keep declaration
   * order. See {@link SearchOrder}.
   */
  order?: SearchOrder;
  /** Which tab this section answers under; defaults to `app` (见 {@link SearchScope}). */
  scope?: SearchScope;
  /** Availability condition (`evaluateWhen` grammar), like any other face. */
  when?: string;
  /**
   * Runtimes this section exists on, defaulting to the module's own `runtimes`.
   * Same narrowing and same reason as {@link CommandContribution.runtimes}: a
   * module whose web service cannot answer this section would otherwise have
   * the tab listed and only fail once queried.
   */
  runtimes?: ModuleRuntime[];
}

/** A {@link SearchContribution} annotated with its declaring module. */
export interface ResolvedSearchSection extends SearchContribution {
  moduleId: string;
  /** Stable `${moduleId}:${id}`, unique across modules — the renderer section id. */
  key: string;
}

/**
 * The input widgets a filter can be. A **closed set**, and that is the design:
 * the dropdown is platform chrome, so an iframe or external module has nothing
 * to render into it — a filter has to be describable as data or those runtimes
 * could not declare one at all. Same shape and same reason as
 * `contributes.settings`, whose `type` is likewise a fixed vocabulary.
 *
 * Two entries, because two are what has a consumer. A third (a static enum,
 * say) is a new member here, not a new escape hatch.
 */
export type SearchFilterType = 'picker' | 'dateRange';

/**
 * One filter a module offers on its own tab (需求 FR-14 §5.3.2 ③).
 *
 * Declared **per module**, not per section: a tab draws one filter bar, and a
 * per-section declaration would have every section repeat the same two entries
 * only for the platform to dedupe them again. Which sections actually honour a
 * value is the module's call — the platform passes every set value to every
 * section it queries and reads none of them (见 {@link SearchFilterValue}).
 */
export interface SearchFilterContribution {
  /** Filter id, unique within the declaring module; the key its value arrives under. */
  id: string;
  /** Chip label, `%key%` placeholder form. */
  title: string;
  type: SearchFilterType;
  /**
   * `picker` only, and required for it: command supplying the candidate rows.
   * Receives one {@link SearchFilterOptionsRequest} and must resolve to
   * {@link SearchFilterOption}[].
   *
   * A command rather than a static list because the interesting candidate sets
   * are live — IM's "from" is the conversation list, which no manifest can
   * enumerate. Must be declared in the same manifest's `contributes.commands`
   * for the same reason a section's command must (`onCommand:<id>` is the only
   * thing that can pull an inactive module up to answer); `validateManifest`
   * enforces it.
   */
  optionsCommand?: string;
  /** `picker` only: allow several candidates at once. Defaults to false. */
  multiple?: boolean;
  /** Ascending display order among this module's filters; ties keep declaration order. */
  order?: number;
  /** Availability condition (`evaluateWhen` grammar), like any other face. */
  when?: string;
  /** Runtimes this filter exists on, defaulting to the module's own `runtimes`. */
  runtimes?: ModuleRuntime[];
}

/** A {@link SearchFilterContribution} annotated with its declaring module. */
export interface ResolvedSearchFilter extends SearchFilterContribution {
  moduleId: string;
  /** Stable `${moduleId}:${id}`, unique across modules. */
  key: string;
}

/** The single argument a `picker` filter's `optionsCommand` receives. */
export interface SearchFilterOptionsRequest {
  /** What the user typed into the picker's own search box; empty means "browse". */
  query: string;
  /** Most rows the caller will show. A hint, as on {@link ModuleSearchRequest}. */
  limit?: number;
}

/** One candidate row a `picker` filter offers. */
export interface SearchFilterOption {
  /** Stable id; this is what lands in {@link SearchFilterValue}'s `ids`. */
  readonly id: string;
  readonly label: string;
  readonly subtitle?: string;
  /** Optional glyph. Not normalized — supply an absolute url (同 {@link ModuleSearchHit.icon}). */
  readonly icon?: IconSpec;
  /**
   * When this row names a person, their id. The panel does not interpret it;
   * a loaded module UI may adorn the label via {@link setSearchFilterOptionAffix}.
   */
  readonly personId?: string;
}

/**
 * A filter's current value, as it crosses to the module.
 *
 * **The platform does not interpret these.** It knows how to collect a date
 * range and how to collect a set of ids, and it knows which filter id they
 * belong to — nothing about what "from" or "date" mean, or which of a module's
 * sections care. That is the whole reason the value is tagged by input type
 * rather than by purpose.
 */
export type SearchFilterValue =
  | { readonly type: 'picker'; readonly ids: readonly string[] }
  | {
      readonly type: 'dateRange';
      /** Inclusive lower bound, epoch ms. Absent means open-ended. */
      readonly from?: number;
      /** Inclusive upper bound, epoch ms. Absent means open-ended. */
      readonly to?: number;
    };

/** The single argument a section's command receives. */
export interface ModuleSearchRequest {
  /** Raw user input for this tab, prefix already stripped by the platform. */
  query: string;
  /**
   * Most rows the caller will show. A hint: the platform truncates anyway, and
   * this exists so a module does not fetch hundreds of rows to be discarded.
   */
  limit?: number;
  /**
   * Values of this module's filters that the user actually set, keyed by the
   * declared filter id. A cleared filter is **absent**, not present-and-empty,
   * so `filters?.from` reads as "did the user narrow by conversation".
   */
  filters?: Readonly<Record<string, SearchFilterValue>>;
}

/**
 * Where a hit's screen row was, handed to an `inPlace` command as a **trailing
 * argument the platform appends** (见 {@link ModuleSearchAction}).
 *
 * A plain rect rather than a DOM node because it crosses to the extension host.
 * Viewport coordinates, CSS pixels — the same frame `getBoundingClientRect`
 * reports and `OverlayAnchoredRequest` consumes, so a module can pass it
 * straight back to `overlay.openModal` without understanding it.
 */
export interface SearchHitAnchor {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** The envelope shape, so both sides read the same key. */
export interface SearchHitAnchorEnvelope {
  readonly anchor: SearchHitAnchor;
}

/**
 * What choosing a module's hit does. Serializable only — a hit crosses a
 * process boundary, so unlike the renderer's own rows it cannot carry a
 * closure. `command` covers "open this thing" for a module that already has a
 * public command for it (IM's `im.openChat`); `openView` covers plain view
 * activation.
 */
export type ModuleSearchAction =
  | {
      readonly type: 'command';
      readonly command: string;
      readonly args?: readonly unknown[];
      /**
       * "Show something **next to this row**, don't navigate away."
       *
       * Two effects, both the platform's: the dropdown **stays open**, and the
       * row's viewport rect is appended to `args` as a
       * {@link SearchHitAnchorEnvelope}. A module reads it off the last
       * argument and hands it to `overlay.openModal({ anchor })` — the anchored
       * presenter (见 `OverlayRequest`) then puts the layer there.
       *
       * For hits whose answer is a *glance* rather than a destination: a
       * person, a group. Opening a profile card by throwing away the query the
       * user just typed is the wrong trade, and it is why this flag exists
       * rather than every such module inventing its own centred modal.
       *
       * The command route (not a renderer-side contribution) is deliberate: it
       * is the only one with `onCommand:<id>` lazy activation behind it, so a
       * hit stays clickable in a module the user has never opened — the same
       * guarantee that makes the section answerable in the first place.
       */
      readonly inPlace?: boolean;
    }
  | {
      readonly type: 'openView';
      readonly viewId: string;
      readonly instanceKey?: string;
    };

/**
 * Read the anchor a platform `inPlace` dispatch appended, from a command's
 * arguments.
 *
 * Lives here rather than in each module because the envelope is the platform's
 * shape: a module that mis-reads it would silently get an un-anchored layer
 * (the worst symptom — a card in the wrong place looks like a styling bug).
 * Returns `undefined` when the command was invoked some other way, which is
 * legitimate: the same command is also reachable from a module's own UI.
 */
export function readSearchHitAnchor(
  args: readonly unknown[],
): SearchHitAnchor | undefined {
  const last = args[args.length - 1];
  if (!last || typeof last !== 'object') {
    return undefined;
  }
  const anchor = (last as Partial<SearchHitAnchorEnvelope>).anchor;
  if (!anchor || typeof anchor !== 'object') {
    return undefined;
  }
  const rect = anchor as Partial<SearchHitAnchor>;
  return typeof rect.x === 'number' &&
    typeof rect.y === 'number' &&
    typeof rect.width === 'number' &&
    typeof rect.height === 'number'
    ? { x: rect.x, y: rect.y, width: rect.width, height: rect.height }
    : undefined;
}

/** One row a module returns for a section. */
export interface ModuleSearchHit {
  /** Unique within the section; the platform keys rows and recent picks by it. */
  readonly id: string;
  readonly title: string;
  readonly subtitle?: string;
  /**
   * Optional glyph. **Not normalized**: the relative-path resolution that
   * `listContributions` applies only runs over manifest icons, so a runtime hit
   * must supply an absolute url itself (a server avatar already is one).
   */
  readonly icon?: IconSpec;
  /** Trailing chip, e.g. a timestamp or the owning conversation. */
  readonly badge?: string;
  /**
   * When this row names a person, their id. The face does not interpret it;
   * a loaded module UI may adorn the title via {@link setSearchFilterOptionAffix}.
   */
  readonly personId?: string;
  /** Relevance within the section; higher sorts first. Defaults to 0. */
  readonly score?: number;
  readonly action: ModuleSearchAction;
}

function isSearchAction(value: unknown): value is ModuleSearchAction {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const action = value as ModuleSearchAction;
  if (action.type === 'command') {
    return typeof action.command === 'string' && action.command.length > 0;
  }
  if (action.type === 'openView') {
    return typeof action.viewId === 'string' && action.viewId.length > 0;
  }
  return false;
}

/**
 * Validate what a section's command resolved to.
 *
 * Throws rather than dropping bad rows: a command crossing the RPC boundary
 * returns `unknown`, and treating a malformed answer as "no hits" would make a
 * broken module look like an empty index. The caller turns the throw into a
 * visible error row for that section.
 */
export function parseModuleSearchHits(value: unknown): ModuleSearchHit[] {
  if (!Array.isArray(value)) {
    throw new Error(
      `search section returned ${value === null ? 'null' : typeof value}, expected an array of hits`,
    );
  }
  return value.map((row, index) => {
    if (!row || typeof row !== 'object') {
      throw new Error(`search hit ${index} is not an object`);
    }
    const hit = row as ModuleSearchHit;
    if (typeof hit.id !== 'string' || hit.id.length === 0) {
      throw new Error(`search hit ${index} is missing an id`);
    }
    if (typeof hit.title !== 'string') {
      throw new Error(`search hit "${hit.id}" is missing a title`);
    }
    if (!isSearchAction(hit.action)) {
      throw new Error(`search hit "${hit.id}" has no usable action`);
    }
    if (
      hit.personId !== undefined &&
      (typeof hit.personId !== 'string' || hit.personId.length === 0)
    ) {
      throw new Error(`search hit "${hit.id}" has an invalid personId`);
    }
    return hit;
  });
}

/**
 * Validate what a `picker` filter's `optionsCommand` resolved to.
 *
 * Throws for the same reason {@link parseModuleSearchHits} does: a malformed
 * answer that degraded into "no candidates" would render as an empty picker,
 * which is exactly what a module with genuinely nothing to offer looks like.
 */
export function parseSearchFilterOptions(value: unknown): SearchFilterOption[] {
  if (!Array.isArray(value)) {
    throw new Error(
      `filter options returned ${value === null ? 'null' : typeof value}, expected an array`,
    );
  }
  return value.map((row, index) => {
    if (!row || typeof row !== 'object') {
      throw new Error(`filter option ${index} is not an object`);
    }
    const option = row as SearchFilterOption;
    if (typeof option.id !== 'string' || option.id.length === 0) {
      throw new Error(`filter option ${index} is missing an id`);
    }
    if (typeof option.label !== 'string') {
      throw new Error(`filter option "${option.id}" is missing a label`);
    }
    if (
      option.personId !== undefined &&
      (typeof option.personId !== 'string' || option.personId.length === 0)
    ) {
      throw new Error(`filter option "${option.id}" has an invalid personId`);
    }
    return option;
  });
}

/**
 * Drop the filters that carry no constraint, so a module can read presence as
 * intent (见 {@link ModuleSearchRequest.filters}).
 *
 * A picker with nothing ticked and a date range with neither end set are both
 * "the user opened this and set nothing" — forwarding them would make every
 * section see two always-present keys it has to bottom out itself.
 */
export function pruneSearchFilterValues(
  values: Readonly<Record<string, SearchFilterValue>>,
): Record<string, SearchFilterValue> {
  const pruned: Record<string, SearchFilterValue> = {};
  for (const [id, value] of Object.entries(values)) {
    if (value.type === 'picker' ? value.ids.length > 0 : hasBound(value)) {
      pruned[id] = value;
    }
  }
  return pruned;
}

function hasBound(value: { from?: number; to?: number }): boolean {
  return value.from !== undefined || value.to !== undefined;
}
